/**
 * Practices data module
 * Fetches data from remote API with fallback to cache
 */

import { apiClient, ApiError } from "@/lib/api/client";
import type { Translation, PracticeServiceTypeDto, PracticeAddress } from "@/lib/api/client";

// ===================================================
// TYPES
// ===================================================

export interface PracticeServiceResponse {
    id: string;
    name: Translation[];
    typeId: string;
    type: PracticeServiceTypeDto;
}

export interface PracticeTrainerResponse {
    id: string;
    name?: string;
    email?: string;
}

export interface PracticeLocationResponse {
    id: string;
    address: PracticeAddress;
    phone?: string;
    email?: string;
    siteUrl?: string;
    practiceId: string;
    isActive?: boolean;
}

export interface Practice {
    id: string;
    practiceId?: string;
    name: string;
    phone?: string;
    email?: string;
    specialization?: string;
    context?: string;
    logoUrl?: string;
    services: PracticeServiceResponse[];
    users?: PracticeTrainerResponse[];
    locations: PracticeLocationResponse[];
}

// ===================================================
// HELPERS
// ===================================================

export function getLocationAddress(location: PracticeLocationResponse): string {
    const addr = location.address || {};
    const parts = [
        [addr.street, addr.building].filter(Boolean).join(" "),
        [addr.zip, addr.city].filter(Boolean).join(" "),
        addr.state,
        addr.country,
    ].filter(Boolean);
    return parts.join(", ");
}

export function getLocationCoords(location: PracticeLocationResponse): { lat: number; lng: number } {
    return {
        lat: location.address?.latitude || 0,
        lng: location.address?.longitude || 0,
    };
}

// ===================================================
// CACHE FOR PRACTICES DATA
// ===================================================

let practicesCache: Practice[] | null = null;
let cacheTimestamp: number = 0;
const CACHE_TTL = 5 * 60 * 1000; // 5 minutes

/**
 * Check if cache is valid
 */
function isCacheValid(): boolean {
    return practicesCache !== null && Date.now() - cacheTimestamp < CACHE_TTL;
}

/**
 * Clear the practices cache
 */
export function clearPracticesCache(): void {
    practicesCache = null;
    cacheTimestamp = 0;
}

// ===================================================
// API FUNCTIONS
// ===================================================

/**
 * Fetches all active practices from the API
 * Uses caching to avoid repeated requests
 * @param forceRefresh - If true, bypasses cache
 * @returns Promise<Practice[]>
 */
export async function getPractices(forceRefresh = false): Promise<Practice[]> {
    if (!forceRefresh && isCacheValid()) {
        return practicesCache!;
    }

    try {
        const practices = await apiClient.getActivePractices();
        practicesCache = practices;
        cacheTimestamp = Date.now();
        return practices;
    } catch (error) {
        // If we have cached data, return it even if stale
        if (practicesCache !== null) {
            console.warn("[getPractices] API failed, returning stale cache:", error);
            return practicesCache;
        }
        throw error;
    }
}

// ===================================================
// RE-EXPORT ERROR TYPES
// ===================================================

export { ApiError };
